<template>
  <AuthCard>
    <h1 data-test="password-reset-key-heading">
      {{ tokenFail ? 'Bad Token' : 'Change Password' }}
    </h1>

    <p v-if="tokenFail" data-test="bad-token-message">
      The password reset link was invalid, possibly because it has already been used.
      Please request a
      <router-link :to="{name: 'password_reset'}">
        new password reset
      </router-link>.
    </p>

    <form v-else class="d-flex flex-column gap-2" @submit.prevent="onSubmit">
      <div v-if="formAlertMessages.length" class="c-login__error">
        <Alert
          v-for="(msg, i) in formAlertMessages"
          :key="i"
          :type="msg.type"
          @close="formAlertMessages.splice(i, 1)"
        >
          {{ msg.message }}
        </Alert>
      </div>

      <div class="c-login__input">
        <TextInput
          name="password1"
          label="New Password"
          is-password
          autocomplete="new-password"
          placeholder="New Password"
          hide-label
          required
          autofocus
          :model-value="form.password1"
          :errors="getFieldErrors('password1')"
          data-test="password-reset-key-password1"
          @update:model-value="form.password1 = $event"
        />
      </div>
      <div class="c-login__input mt-2">
        <TextInput
          name="password2"
          label="New Password (again)"
          is-password
          autocomplete="new-password"
          placeholder="New Password (again)"
          hide-label
          required
          :model-value="form.password2"
          :errors="getFieldErrors('password2')"
          data-test="password-reset-key-password2"
          @update:model-value="form.password2 = $event"
        />
      </div>

      <!--
        FontAwesome's dom.watch() replaces <i class="fa"> with <svg> at mount,
        so a reactive :class on the <i> never reaches the rendered SVG. Toggle
        a Vue-controlled wrapper instead and let FA process each icon once.
      -->
      <div class="password-requirements c-login__p-req-container">
        <table>
          <tbody>
            <tr :class="{'is-met': hasValidCharacterMix}">
              <td class="requirement-icon py-0">
                <span v-show="hasValidCharacterMix"><i class="fa fa-check text-success" aria-hidden="true" /></span>
                <span v-show="!hasValidCharacterMix"><i class="fa fa-times text-danger" aria-hidden="true" /></span>
              </td>
              <td class="py-0">
                Contains characters from at least 2 of these categories: uppercase or
                lowercase letters, numbers and special characters (ex, !, @, #, $, %, ^, &amp;, *)
              </td>
            </tr>
            <tr :class="{'is-met': isLongEnough}">
              <td class="requirement-icon py-0">
                <span v-show="isLongEnough"><i class="fa fa-check text-success" aria-hidden="true" /></span>
                <span v-show="!isLongEnough"><i class="fa fa-times text-danger" aria-hidden="true" /></span>
              </td>
              <td class="py-0">
                8 or more characters
              </td>
            </tr>
            <tr :class="{'is-met': passwordsMatch}">
              <td class="requirement-icon py-0">
                <span v-show="passwordsMatch"><i class="fa fa-check text-success" aria-hidden="true" /></span>
                <span v-show="!passwordsMatch"><i class="fa fa-times text-danger" aria-hidden="true" /></span>
              </td>
              <td class="py-0">
                Passwords are the same
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <button
        type="submit"
        class="btn btn-primary mt-3 w-100"
        :disabled="submitting"
        data-test="password-reset-key-submit"
      >
        {{ submitting ? 'Saving…' : 'Change Password' }}
      </button>
    </form>
  </AuthCard>
</template>

<script setup>
import {inject, reactive, ref, toRef} from 'vue';
import {useRoute, useRouter} from 'vue-router';
import * as Sentry from '@sentry/browser';
import AuthCard from './components/AuthCard.vue';
import Alert from '@canopyllc/roots/components/Alert.vue';
import TextInput from '@canopyllc/roots/components/forms/TextInput.vue';
import {usePasswordChecks} from '@/js/composables/usePasswordChecks.js';
import {parseAllauthErrors} from '@canopyllc/roots/utils/allauthErrors.js';
import {useErrorManager} from '@canopyllc/roots/composables/errorManager.js';

const api = inject('api'),
      urls = inject('urls'),
      route = useRoute(),
      router = useRouter(),

      form = reactive({password1: '', password2: ''}),
      submitting = ref(false),
      tokenFail = ref(false),

      {hasValidCharacterMix, isLongEnough, passwordsMatch} = usePasswordChecks(
        toRef(form, 'password1'),
        toRef(form, 'password2'),
      ),

      {
        formAlertMessages, getFieldErrors, clearFormErrors, setFormFieldErrors, setAllauthErrors, setNonFieldErrors,
      } = useErrorManager();

async function onSubmit() {
  clearFormErrors();
  if (!passwordsMatch.value) {
    setFormFieldErrors({password2: ['You must type the same password each time.']});
    return;
  }

  submitting.value = true;
  try {
    await api.post(urls.passwordResetApiUrl, {
      key: route.params.key,
      password: form.password1,
    });
    router.push({name: 'password_reset_from_key_done'});
  } catch (err) {
    const status = err?.response?.status;
    // allauth.headless signs the user in on a successful key reset, so a
    // 401 response on the second attempt (e.g. retry after success) means
    // "you're already authenticated for this session" — treat as success.
    if (status === 401) {
      router.push({name: 'password_reset_from_key_done'});
      return;
    }
    if (status === 400) {
      // allauth returns the new-password errors on `password`; PEARS shows
      // them on the first of two password fields.
      const parsed = parseAllauthErrors(err.response.data, {login: 'email', password: 'password1'});
      if (parsed.key) {
        tokenFail.value = true;
      } else {
        setAllauthErrors(parsed);
      }
    } else {
      Sentry.captureException(err);
      setNonFieldErrors([{message: 'Something went wrong. Please try again.', type: 'danger'}]);
    }
    submitting.value = false;
  }
}
</script>
